import React from "react";
import { NavLink } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { usePortal } from "@/pages/portal/PortalContext";

const BASE_LINKS = [
  { to: "/portal/overview",   label: "Overview" },
  { to: "/portal/tour",       label: "My Tour" },
  { to: "/portal/routing",    label: "Routing" },
  { to: "/portal/quotes",     label: "Quotes" },
  { to: "/portal/documents",  label: "Documents" },
];

export const PortalTopNav = () => {
  const { me, activeTour, billing, signOut } = usePortal();
  // Only surface the Billing tab when there is an authoritative,
  // customer-facing billing state (i.e. an executed contract exists).
  // Otherwise it stays hidden — no empty tab flash.
  const LINKS = billing && billing.has_authoritative
    ? [...BASE_LINKS.slice(0, 4), { to: "/portal/billing", label: "Billing" }, BASE_LINKS[4]]
    : BASE_LINKS;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <header
      className="hidden md:block sticky top-0 z-40"
      style={{ background: "var(--bg-nav)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      data-testid="portal-top-nav"
    >
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8 h-[72px] flex items-center gap-6">
        {/* Left — logo + eyebrow */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-col">
            <BrandLogo forceDark height={30} withOpsMarker={false} testid="portal-brand-logo" />
            <div className="mt-1 uppercase text-[10.5px] tracking-[0.18em] font-semibold"
                 style={{ color: "#B7E31D" }} data-testid="portal-eyebrow">
              Client Portal
            </div>
          </div>
        </div>

        {/* Center — nav links */}
        <nav className="flex-1 flex justify-center items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`portal-nav-${l.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `relative inline-flex items-center h-[72px] px-4 text-[13.5px] font-medium transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white/90"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-2 right-2 bottom-3 h-[2px] rounded"
                          style={{ background: "#B7E31D" }} />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right — theme + identity + menu */}
        <div className="flex items-center gap-3 shrink-0">
          <ThemeToggle compact />

          <div className="hidden lg:flex flex-col text-right leading-tight max-w-[240px]">
            <span className="text-[13px] font-medium text-white truncate" data-testid="portal-client-name">
              {me?.display_name || "Client"}
            </span>
            {activeTour?.display_name && (
              <span className="text-[11px] text-white/55 truncate">
                {activeTour.display_name}
              </span>
            )}
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-[12.5px] text-white/80 hover:text-white transition-colors border border-white/10 hover:border-white/20"
              data-testid="portal-account-menu-btn"
            >
              Account
              <ChevronDown size={14} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-11 w-56 rounded-md overflow-hidden shadow-lg"
                style={{
                  background: "var(--bg-elevated)",
                  border: "1px solid var(--border-default)",
                  color: "var(--fg-primary)",
                }}
                data-testid="portal-account-menu"
              >
                <div className="px-3 py-2.5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                  <div className="text-[13px] font-semibold" style={{ color: "var(--fg-primary)" }}>
                    {me?.display_name}
                  </div>
                  <div className="text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
                    Tour Manager
                  </div>
                </div>
                <button
                  onClick={signOut}
                  data-testid="portal-signout"
                  className="w-full text-left px-3 py-2.5 text-[13px] flex items-center gap-2 hover:bg-[color:var(--bg-inset)]"
                  style={{ color: "var(--fg-primary)" }}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Subtle inset accent at the very bottom */}
        <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(183,227,29,0.35), transparent)" }} />
      </div>
    </header>
  );
};
